import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@/lib/api";

/**
 * Neuralcraft Helper — a lightweight conversational assistant floating in
 * the bottom-right corner of the Console. Explicitly NOT a workflow runner;
 * see backend /api/helper/chat.
 *
 * Session-only: state lives entirely in this component; a page refresh
 * clears it. No cross-run memory.
 */

function timeStr() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

const SUGGESTIONS = [
  "What is Neuralcraft?",
  "Help me phrase a goal",
  "What can the 11 agents do?",
];

export default function HelperChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);        // [{role, content, ts}]
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll on new message + focus input when opened
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, busy]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  async function sendMessage(text) {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setErr("");
    const now = timeStr();
    const userMsg = { role: "user", content, ts: now };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput("");
    setBusy(true);

    try {
      const { data } = await api.post("/helper/chat", {
        messages: next.map((m) => ({ role: m.role, content: m.content })),
      });
      setMessages([...next, { role: "assistant", content: data.reply, ts: timeStr() }]);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      let detail;
      if (Array.isArray(raw)) {
        // Pydantic 422 returns an array of {loc, msg, type} objects
        detail = raw.map((r) => r?.msg || String(r)).join("; ");
      } else {
        detail = raw ? String(raw) : "Helper is temporarily unavailable.";
      }
      setErr(detail);
    } finally {
      setBusy(false);
    }
  }

  function onKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  function clearSession() {
    setMessages([]);
    setErr("");
    setInput("");
  }

  return (
    <>
      {/* Collapsed pill — bottom-right, unobtrusive */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="helper-pill"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            onClick={() => setOpen(true)}
            data-testid="helper-open-btn"
            className="fixed bottom-6 right-6 z-30 group"
            style={{
              background: "var(--bg-2)",
              border: "1px solid var(--line-strong)",
              padding: "10px 16px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              cursor: "pointer",
            }}
          >
            <span className="relative flex" aria-hidden>
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: "var(--accent)" }}
              />
              <span className="pulse-ring" />
            </span>
            <span
              className="font-mono uppercase"
              style={{
                fontSize: "0.72rem",
                letterSpacing: "0.22em",
                color: "var(--ink)",
              }}
            >
              Helper
            </span>
            <span
              className="font-mono opacity-70 group-hover:opacity-100"
              style={{
                fontSize: "0.66rem",
                letterSpacing: "0.18em",
                color: "var(--ink-2)",
              }}
            >
              ask anything
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Expanded panel */}
      <AnimatePresence>
        {open && (
          <motion.aside
            key="helper-panel"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ type: "tween", duration: 0.22 }}
            data-testid="helper-panel"
            className="fixed bottom-6 right-6 z-30 flex flex-col"
            style={{
              width: "min(400px, calc(100vw - 3rem))",
              height: "min(560px, calc(100vh - 8rem))",
              background: "var(--bg-2)",
              border: "1px solid var(--line-strong)",
              boxShadow: "0 24px 60px -20px rgba(0, 0, 0, 0.6)",
            }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-5 py-4 border-b hairline"
              style={{ background: "var(--bg)" }}
            >
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-2">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: "var(--accent)", boxShadow: "0 0 0 4px var(--accent-soft)" }}
                  />
                  <span
                    className="font-serif-display italic"
                    style={{ fontSize: "1.05rem", color: "var(--ink)" }}
                  >
                    Neuralcraft Helper
                  </span>
                </div>
                <span className="label-xs">Ask anything · session only</span>
              </div>
              <div className="flex items-center gap-3">
                {messages.length > 0 && (
                  <button
                    onClick={clearSession}
                    className="font-mono uppercase"
                    style={{
                      fontSize: "0.66rem",
                      letterSpacing: "0.2em",
                      color: "var(--ink-2)",
                    }}
                    data-testid="helper-clear-btn"
                    title="Clear conversation"
                  >
                    Clear
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="font-mono uppercase"
                  style={{
                    fontSize: "0.66rem",
                    letterSpacing: "0.2em",
                    color: "var(--ink-2)",
                  }}
                  data-testid="helper-close-btn"
                  title="Close"
                >
                  Close
                </button>
              </div>
            </div>

            {/* Body — message list, no bubbles */}
            <div
              ref={bodyRef}
              className="flex-1 overflow-y-auto px-5 py-4 flex flex-col"
              data-testid="helper-body"
              style={{ gap: 0 }}
            >
              {messages.length === 0 && (
                <div className="flex-1 flex flex-col justify-center">
                  <div
                    className="font-serif-display italic mb-3"
                    style={{ fontSize: "1.4rem", color: "var(--ink)", lineHeight: 1.25 }}
                  >
                    How can I help you<br />
                    <span className="brand-gradient">work sharper?</span>
                  </div>
                  <p style={{ fontSize: "0.85rem", color: "var(--ink-2)", lineHeight: 1.55 }}>
                    I can explain how Neuralcraft works, help you turn a fuzzy idea into a
                    one-sentence goal, or handle small tasks like rewriting a line. For real
                    multi-step work, use the main <b style={{ color: "var(--ink)" }}>Dispatch goal</b> box.
                  </p>
                  <div className="mt-5 flex flex-col gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        onClick={() => sendMessage(s)}
                        data-testid={`helper-suggestion-${s.replace(/\s+/g, "-").toLowerCase()}`}
                        className="text-left font-mono uppercase"
                        style={{
                          fontSize: "0.68rem",
                          letterSpacing: "0.18em",
                          color: "var(--ink)",
                          border: "1px solid var(--line)",
                          padding: "10px 12px",
                          background: "transparent",
                        }}
                      >
                        <span style={{ color: "var(--accent)" }}>›&nbsp;</span> {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, i) => (
                <div
                  key={i}
                  className={i > 0 ? "pt-4 mt-4" : "mt-0"}
                  style={{
                    borderTop: i > 0 ? "1px solid var(--line)" : "none",
                  }}
                  data-testid={`helper-msg-${i}`}
                >
                  <div
                    className="flex items-center gap-2 mb-1.5"
                    style={{ color: m.role === "user" ? "var(--accent)" : "var(--ink-2)" }}
                  >
                    <span
                      className="font-mono uppercase"
                      style={{ fontSize: "0.62rem", letterSpacing: "0.24em" }}
                    >
                      {m.role === "user" ? "You" : "Helper"}
                    </span>
                    <span
                      className="font-mono"
                      style={{ fontSize: "0.62rem", color: "var(--ink-3)" }}
                    >
                      · {m.ts}
                    </span>
                  </div>
                  <p
                    style={{
                      fontSize: "0.92rem",
                      lineHeight: 1.55,
                      color: "var(--ink)",
                      whiteSpace: "pre-wrap",
                      margin: 0,
                    }}
                  >
                    {m.content}
                  </p>
                </div>
              ))}

              {busy && (
                <div
                  className={"pt-4 mt-4"}
                  style={{ borderTop: messages.length ? "1px solid var(--line)" : "none" }}
                  data-testid="helper-thinking"
                >
                  <div className="flex items-center gap-2 mb-1.5" style={{ color: "var(--ink-2)" }}>
                    <span
                      className="font-mono uppercase"
                      style={{ fontSize: "0.62rem", letterSpacing: "0.24em" }}
                    >
                      Helper
                    </span>
                  </div>
                  <div className="flex items-center gap-2" style={{ color: "var(--ink-2)" }}>
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: "var(--accent)", animation: "blink 1s steps(1) infinite" }}
                    />
                    <span style={{ fontSize: "0.85rem" }}>thinking…</span>
                  </div>
                </div>
              )}

              {err && (
                <div
                  className="mt-4 pt-4 font-mono"
                  style={{
                    borderTop: "1px solid var(--line)",
                    fontSize: "0.75rem",
                    color: "var(--err)",
                  }}
                  data-testid="helper-error"
                >
                  {err}
                </div>
              )}
            </div>

            {/* Input */}
            <div
              className="border-t hairline px-5 py-3"
              style={{ background: "var(--bg)" }}
            >
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  disabled={busy}
                  placeholder="Ask a question or paste a rough goal…"
                  className="flex-1"
                  data-testid="helper-input"
                  style={{
                    background: "transparent",
                    border: "none",
                    outline: "none",
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: "0.85rem",
                    color: "var(--ink)",
                    padding: "6px 0",
                  }}
                  maxLength={4000}
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={busy || !input.trim()}
                  data-testid="helper-send-btn"
                  className="font-mono uppercase"
                  style={{
                    fontSize: "0.66rem",
                    letterSpacing: "0.24em",
                    padding: "8px 14px",
                    background: input.trim() && !busy ? "var(--accent)" : "transparent",
                    color: input.trim() && !busy ? "var(--bg)" : "var(--ink-3)",
                    border: "1px solid " + (input.trim() && !busy ? "var(--accent)" : "var(--line)"),
                    cursor: input.trim() && !busy ? "pointer" : "not-allowed",
                    transition: "all 120ms ease",
                  }}
                >
                  Send
                </button>
              </div>
              <div
                className="mt-2 font-mono"
                style={{
                  fontSize: "0.6rem",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "var(--ink-3)",
                }}
              >
                Session only · not saved to Runs
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
